/**
 * 🎓 TICKET RVDPQ5 - UI Alumno: Vista de recurso embebido (YouTube) + continuidad
 * 🎓 TICKET MTSKJ2 - Marcar actividad como completada al hacer click en "Continuar"
 * 🎓 TICKET GHTVN9 - Tracking automático de tiempo invertido en actividades
 * 
 * Muestra recursos (videos YouTube, links) embebidos dentro del sistema
 * con breadcrumb, navegación continua y contexto del curso.
 * 
 * Ruta: /courses/:courseId/activity/:activityId
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Play, ExternalLink } from 'lucide-react';
import { getPublishedCourseById } from '../services/coursesApi';
import { markActivityComplete } from '../services/courseProgressApi'; // 🎓 MTSKJ2
import { trackActivityTime } from '../services/analyticsApi'; // 🎓 GHTVN9

const CourseResourcePage = () => {
  const { courseId, activityId } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [activity, setActivity] = useState(null);
  const [module, setModule] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextActivity, setNextActivity] = useState(null);

  // 🎓 TICKET GHTVN9 - Tracking de tiempo
  const startTimeRef = useRef(null);

  useEffect(() => {
    loadCourseAndActivity();

    // 🎓 GHTVN9: Iniciar contador de tiempo al montar
    startTimeRef.current = Date.now();
    console.log('⏱️ GHTVN9: Iniciando tracking de tiempo para', activityId);

    // Cleanup: Enviar tiempo al desmontar componente
    return () => {
      if (startTimeRef.current) {
        const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
        if (elapsedSeconds > 0) {
          trackActivityTime(courseId, activityId, elapsedSeconds)
            .catch(err => console.warn('⚠️ GHTVN9: No se pudo registrar tiempo al desmontar', err));
        }
      }
    };
  }, [courseId, activityId]);

  const loadCourseAndActivity = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await getPublishedCourseById(courseId);
      setCourse(data);

      // Buscar la actividad en todos los módulos
      let foundActivity = null;
      let foundModule = null;
      let foundNextActivity = null;

      for (const mod of data.modules) {
        const activityIndex = mod.activities.findIndex(a => a.activityId === activityId);
        
        if (activityIndex !== -1) {
          foundActivity = mod.activities[activityIndex];
          foundModule = mod;

          // Buscar siguiente actividad en el mismo módulo
          if (activityIndex < mod.activities.length - 1) {
            foundNextActivity = {
              activity: mod.activities[activityIndex + 1],
              module: mod
            };
          } else {
            // Buscar en siguiente módulo
            const currentModuleIndex = data.modules.findIndex(m => m.moduleId === mod.moduleId);
            if (currentModuleIndex < data.modules.length - 1) {
              const nextModule = data.modules[currentModuleIndex + 1];
              if (nextModule.activities && nextModule.activities.length > 0) {
                foundNextActivity = {
                  activity: nextModule.activities[0],
                  module: nextModule
                };
              }
            }
          }
          
          break;
        }
      }

      if (!foundActivity) {
        setError('Recurso no encontrado en el curso.');
        setIsLoading(false);
        return;
      }

      // Validar que sea tipo resource
      if (foundActivity.type !== 'resource') {
        setError('Esta actividad no es un recurso.');
        setIsLoading(false);
        return;
      }

      setActivity(foundActivity);
      setModule(foundModule);
      setNextActivity(foundNextActivity);
      setIsLoading(false);

    } catch (err) {
      console.error('❌ RVDPQ5: Error al cargar recurso:', err);
      setError(err.message || 'No se pudo cargar el recurso.');
      setIsLoading(false);
    }
  };

  /**
   * Extrae VIDEO_ID de una URL de YouTube
   * Soporta formatos:
   * - https://www.youtube.com/watch?v=VIDEO_ID
   * - https://youtu.be/VIDEO_ID
   * - https://www.youtube.com/embed/VIDEO_ID
   */
  const extractYouTubeVideoId = (url) => {
    if (!url) return null;

    const patterns = [
      /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]+)/,
      /(?:youtu\.be\/)([a-zA-Z0-9_-]+)/,
      /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  };

  const handleContinue = async () => {
    if (!nextActivity) return;

    // 🎓 TICKET GHTVN9 - Enviar tiempo antes de navegar
    if (startTimeRef.current) {
      const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
      if (elapsedSeconds > 0) {
        try {
          await trackActivityTime(courseId, activityId, elapsedSeconds);
          console.log(`⏱️ GHTVN9: Tiempo registrado - ${elapsedSeconds}s`);
        } catch (err) {
          console.warn('⚠️ GHTVN9: No se pudo registrar tiempo', err);
        }
      }
      // Reset para evitar doble conteo en cleanup
      startTimeRef.current = null;
    }

    // 🎓 TICKET MTSKJ2 - Marcar actividad actual como completada
    try {
      await markActivityComplete(courseId, activityId);
      console.log('✅ MTSKJ2: Actividad marcada como completada:', activityId);
    } catch (err) {
      console.error('❌ MTSKJ2: Error al marcar actividad:', err);
      // No bloquear navegación por error de tracking
    }

    const { activity: next, module: nextMod } = nextActivity;

    console.log('🎓 RVDPQ5: Continuando a siguiente actividad:', {
      activityId: next.activityId,
      type: next.type,
      moduleId: nextMod.moduleId
    });

    // Navegar según tipo de siguiente actividad
    switch (next.type) {
      case 'resource':
        navigate(`/courses/${courseId}/activity/${next.activityId}`);
        break;
      
      case 'vrt_simulation':
        if (!next.refId) {
          alert('Error: La siguiente actividad VRT no tiene agente asignado.');
          return;
        }
        navigate(`/vertex/simulator?agentId=${next.refId}&fromCourseId=${courseId}&fromActivityId=${next.activityId}`);
        break;
      
      case 'epl_lab':
        if (!next.refId) {
          alert('Error: La siguiente actividad EPL no tiene lab asignado.');
          return;
        }
        navigate(`/epl/lab/${next.refId}?fromCourseId=${courseId}&fromActivityId=${next.activityId}`);
        break;
      
      default:
        alert('Tipo de siguiente actividad no soportado.');
    }
  };

  // 🎓 Estado: Loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando recurso...</p>
        </div>
      </div>
    );
  }

  // 🎓 Estado: Error
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExternalLink className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error al cargar recurso</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to={`/courses/${courseId}`}
            className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al curso
          </Link>
        </div>
      </div>
    );
  }

  // Render del recurso según subtype
  const renderResource = () => {
    if (activity.resource.subtype === 'youtube') {
      const videoId = extractYouTubeVideoId(activity.resource.youtubeUrl);
      
      if (!videoId) {
        return (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <p className="text-yellow-800 mb-4">No se pudo extraer el ID del video de YouTube.</p>
            <a
              href={activity.resource.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <ExternalLink className="w-4 h-4" />
              Abrir en YouTube
            </a>
          </div>
        );
      }

      return (
        <div className="relative w-full" style={{ paddingBottom: '56.25%' /* 16:9 aspect ratio */ }}>
          <iframe
            className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg"
            src={`https://www.youtube.com/embed/${videoId}`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={activity.title}
          />
        </div>
      );
    }

    if (activity.resource.subtype === 'link') {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
          <ExternalLink className="w-12 h-12 text-blue-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Enlace externo</h3>
          <p className="text-gray-600 mb-6">Este recurso te llevará a un sitio externo.</p>
          <a
            href={activity.resource.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <ExternalLink className="w-5 h-5" />
            Abrir enlace
          </a>
        </div>
      );
    }

    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <p className="text-gray-600">Tipo de recurso no soportado: {activity.resource.subtype}</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <nav className="flex items-center gap-2 text-sm text-gray-600">
          <Link to="/dashboard_cursos_disponibles" className="hover:text-blue-600 transition-colors">
            Cursos
          </Link>
          <ChevronRight className="w-4 h-4" />
          <Link to={`/courses/${courseId}`} className="hover:text-blue-600 transition-colors">
            {course?.title}
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900">{module?.title}</span>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900 font-medium">{activity?.title}</span>
        </nav>
      </div>

      {/* Contenido principal */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            {activity.resource.subtype === 'youtube' ? (
              <Play className="w-6 h-6 text-blue-600" />
            ) : (
              <ExternalLink className="w-6 h-6 text-blue-600" />
            )}
            <h1 className="text-3xl font-bold text-gray-900">{activity.title}</h1>
          </div>
          
          {activity.estimatedMinutes && (
            <p className="text-gray-600">
              Duración estimada: {activity.estimatedMinutes} minutos
            </p>
          )}
        </div>

        {/* Recurso embebido */}
        <div className="mb-8">
          {renderResource()}
        </div>

        {/* Botones de navegación */}
        <div className="flex items-center justify-between gap-4">
          <Link
            to={`/courses/${courseId}`}
            className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver al curso
          </Link>

          {nextActivity ? (
            <button
              onClick={handleContinue}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Continuar
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <div className="text-center">
              <p className="text-gray-600 mb-3">Has completado este módulo</p>
              <Link
                to={`/courses/${courseId}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                <ArrowLeft className="w-5 h-5" />
                Volver al curso
              </Link>
            </div>
          )}
        </div>

        {/* Info de siguiente actividad */}
        {nextActivity && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Siguiente:</span>{' '}
              {nextActivity.activity.title}
              {' '}
              <span className="text-blue-600">
                ({nextActivity.activity.type === 'vrt_simulation' ? 'Simulación VRT' : 
                  nextActivity.activity.type === 'epl_lab' ? 'Lab EPL' : 
                  'Recurso'})
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseResourcePage;
